import { clsx } from 'clsx';
import {
  Code,
  CreditCard,
  Database,
  Home,
  LogOut,
  MessageSquare,
  Users,
  Wrench,
} from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import logoClara from '../../assets/logo-clara.svg';
import logoEscura from '../../assets/logo-escura.svg';
import { useAuth, useTheme } from '../../contexts';
import DevSpottSymbolLoader from '../conversation/DevSpottSymbolLoader';

interface SidebarProps {
  isOpen: boolean;
  isCollapsed: boolean;
  onClose: () => void;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  description: string;
  disabled?: boolean;
  badge?: string;
}

const getNavigation = (t: any): NavigationItem[] => [
  {
    name: t('navigation.dashboard'),
    href: '/dashboard',
    icon: Home,
    description: t('navigation.dashboardDesc'),
  },
  {
    name: t('navigation.conversations'),
    href: '/conversations',
    icon: MessageSquare,
    description: t('navigation.conversationsDesc'),
  },
  {
    name: t('navigation.knowledgeBase'),
    href: '/knowledge-base',
    icon: Database,
    description: t('navigation.knowledgeBaseDesc'),
  },
  {
    name: t('navigation.agentTeams'),
    href: '/agent-teams',
    icon: Users,
    description: t('navigation.agentTeamsDesc'),
  },
  {
    name: t('navigation.integrations'),
    href: '/integrations',
    icon: Code,
    description: t('navigation.integrationsDesc'),
  },
  {
    name: t('navigation.tools'),
    href: '/ferramentas',
    icon: Wrench,
    description: t('navigation.toolsDesc'),
  },
  {
    name: t('navigation.subscription'),
    href: '/subscription',
    icon: CreditCard,
    description: t('navigation.subscriptionDesc'),
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, isCollapsed, onClose }) => {
  const location = useLocation();
  const { logout } = useAuth();
  const { theme } = useTheme();
  const { t } = useTranslation();

  const navigation = getNavigation(t);

  const handleLogout = () => {
    logout();
  };

  return (
    <>
      {/* Mobile backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-graphite-900 bg-opacity-75 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={clsx(
          'fixed inset-y-0 left-0 z-50 w-[271px] bg-white dark:bg-graphite-900 shadow-lg transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0',
          isCollapsed ? 'lg:w-20' : 'lg:w-[271px]',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div
            className={clsx(
              'relative flex items-center h-16 border-b border-light-200 dark:border-graphite-700',
              isCollapsed ? 'justify-center px-2' : 'justify-center px-6'
            )}
          >
            {isCollapsed ? (
              <DevSpottSymbolLoader
                size="sm"
                animated={false}
                className="h-9 w-9"
              />
            ) : (
              <img
                src={theme === 'dark' ? logoClara : logoEscura}
                alt="DevSpott AI"
                className="h-10 w-[180px] object-contain"
              />
            )}
            <button
              onClick={onClose}
              className="absolute right-6 lg:hidden text-deepTechBlue-500 dark:text-light-400 hover:text-deepTechBlue-600 dark:hover:text-light-300"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Navigation */}
          <nav
            className={clsx(
              'flex-1 py-6',
              isCollapsed ? 'px-2 space-y-6' : 'px-4 space-y-1'
            )}
          >
            {navigation.map(item => {
              const isActive =
                location.pathname === item.href ||
                location.pathname.startsWith(`${item.href}/`);
              const isDisabled = item.disabled;

              const content = (
                <div
                  className={clsx(
                    'group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-200',
                    isCollapsed && 'justify-center py-4 px-2',
                    isActive ? 'sidebar-item-active' : 'sidebar-item',
                    isCollapsed && isActive && 'border-r-0',
                    isDisabled && 'opacity-50 cursor-not-allowed'
                  )}
                  title={isCollapsed ? item.name : undefined}
                >
                  <item.icon
                    className={clsx(
                      'h-5 w-5 flex-shrink-0',
                      isCollapsed ? '' : 'mr-3',
                      isActive
                        ? 'text-primary-600 dark:text-primary-400'
                        : isDisabled
                          ? 'text-deepTechBlue-300 dark:text-light-600'
                          : 'text-deepTechBlue-500 dark:text-light-500 group-hover:text-deepTechBlue-600 dark:group-hover:text-light-50'
                    )}
                  />
                  <div className={clsx('flex-1', isCollapsed && 'hidden')}>
                    <div className="flex items-center justify-between">
                      <div
                        className={clsx(
                          'font-medium',
                          isActive
                            ? 'text-primary-700 dark:text-primary-400'
                            : isDisabled
                              ? 'text-deepTechBlue-500 dark:text-light-600'
                              : 'text-deepTechBlue-500 dark:text-light-300 group-hover:text-deepTechBlue-500 dark:group-hover:text-light-50'
                        )}
                      >
                        {item.name}
                      </div>
                      {item.badge && (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          {item.badge}
                        </span>
                      )}
                    </div>
                    <div
                      className={clsx(
                        'text-xs',
                        isActive
                          ? 'text-primary-600 dark:text-primary-400'
                          : isDisabled
                            ? 'text-deepTechBlue-500 dark:text-light-600'
                            : 'text-deepTechBlue-500 dark:text-light-400 group-hover:text-deepTechBlue-600 dark:group-hover:text-light-50'
                      )}
                    >
                      {item.description}
                    </div>
                  </div>
                </div>
              );

              if (isDisabled) {
                return (
                  <div key={item.name} className="cursor-not-allowed">
                    {content}
                  </div>
                );
              }

              return (
                <Link key={item.name} to={item.href} onClick={onClose}>
                  {content}
                </Link>
              );
            })}
          </nav>

          {/* Footer */}
          <div
            className={clsx(
              'border-t border-light-200 dark:border-graphite-700',
              isCollapsed ? 'p-2' : 'p-4'
            )}
          >
            <button
              onClick={handleLogout}
              className={clsx(
                'w-full flex items-center px-3 py-2 text-sm font-medium text-deepTechBlue-500 dark:text-light-300 hover:bg-light-50 dark:hover:bg-graphite-800 hover:text-deepTechBlue-500 dark:hover:text-light-50 rounded-lg transition-colors duration-200',
                isCollapsed && 'justify-center'
              )}
              title={isCollapsed ? t('navigation.logout') : undefined}
            >
              <LogOut
                className={clsx(
                  'h-5 w-5 text-deepTechBlue-500 dark:text-light-500',
                  isCollapsed ? '' : 'mr-3'
                )}
              />
              {!isCollapsed ? t('navigation.logout') : null}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
